/**
 * 地图坐标常量与归一化
 *
 * 背景：房源 / 小区的经纬度由后端返回，可能是 null、空串、0 或写反的分量。
 * 直接 `+longitude` 会得到 0 或 NaN，高德周边搜索会以 (0,0) 为圆心返回 no_data，
 * 页面表现为「底图空白 + 暂无数据」，用户无法判断是数据缺失还是周边真的没有 POI。
 *
 * 这里统一兜底到中山市政府（与 `store/modules/auth.ts` 的默认 curLoc 一致），
 * 只用于地图中心与周边搜索，不参与导航（导航走 wx.openLocation，仍是微信内能力）。
 */
export const DEFAULT_MAP_CENTER = {
  longitude: 113.392532, // 中山市政府经度
  latitude: 22.517007, // 中山市政府纬度
} as const

export interface MapLocationLike {
  longitude?: number | string | null
  latitude?: number | string | null
}

/** 单个分量的有效性判断：不可用返回 null。 */
const toDegree = (raw: unknown, limit: number): number | null => {
  // 只接受数字与数字字符串，true / {} / [] 这类一律视为缺失
  if (typeof raw !== 'number' && typeof raw !== 'string') return null
  if (typeof raw === 'string' && raw.trim() === '') return null
  const value = Number(raw)
  if (!Number.isFinite(value)) return null
  // 后端「未填」的哨兵值；中山范围内不存在 0 度经纬度
  if (value === 0) return null
  // 经度 >180 / 纬度 >90，可顺带兜住 latitude/longitude 写反的数据
  if (Math.abs(value) > limit) return null
  return value
}

/**
 * 归一化成地图可直接使用的中心点。
 * 任一分量不可用则**整体**回落，避免出现「真实经度 + 兜底纬度」的错位坐标。
 */
export const resolveMapCenter = (location?: MapLocationLike | null) => {
  const longitude = toDegree(location?.longitude, 180)
  const latitude = toDegree(location?.latitude, 90)
  if (longitude === null || latitude === null) {
    return { ...DEFAULT_MAP_CENTER }
  }
  return { longitude, latitude }
}
